import { useState, useEffect, useCallback } from "react";
import {
  Store, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Clock, Shield, Info, Key, Zap, AlertCircle, Copy, Eye, EyeOff,
} from "lucide-react";
import adminApi from "@/lib/adminApi";

function StatusDot({ status }) {
  const cls =
    status === "ok"      ? "bg-green-400" :
    status === "warn"    ? "bg-yellow-400" :
    status === "bad"     ? "bg-red-400" :
    status === "info"    ? "bg-blue-400" :
                           "bg-white/20";
  return <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${cls}`} />;
}

function DiagRow({ label, value, status, sub }) {
  const textCls =
    status === "ok"   ? "text-green-400" :
    status === "bad"  ? "text-red-400" :
    status === "warn" ? "text-yellow-400" :
    status === "info" ? "text-blue-400" :
                        "text-white/70";
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-white/[0.04] last:border-0 gap-4">
      <span className="text-[11px] text-white/50 uppercase tracking-wider shrink-0">{label}</span>
      <div className="text-right">
        <span className={`text-xs font-medium ${textCls}`}>{value || "—"}</span>
        {sub && <p className="text-[10px] text-white/25 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function TokenCard({ title, present, tokenType, isCorrectType, testOk, hints, description }) {
  const statusOk  = present && isCorrectType && testOk !== false;
  const statusBad = !present || !isCorrectType;

  return (
    <div className={`rounded border p-3 ${statusOk ? "border-green-500/20 bg-green-500/5" : statusBad ? "border-red-500/20 bg-red-500/5" : "border-yellow-500/20 bg-yellow-500/5"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Key size={11} className={statusOk ? "text-green-400" : "text-red-400"} />
        <span className={`text-[10px] font-bold uppercase tracking-wider ${statusOk ? "text-green-400" : statusBad ? "text-red-400" : "text-yellow-400"}`}>{title}</span>
        <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-mono ${statusOk ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
          {present ? tokenType : "missing"}
        </span>
      </div>
      <p className="text-[10px] text-white/35 mb-1">{description}</p>
      {!present && (
        <p className="text-[10px] text-red-400/70 mt-1">
          Not found in env. {hints?.[0] || ""}
        </p>
      )}
      {present && !isCorrectType && (
        <p className="text-[10px] text-yellow-400/70 mt-1">
          Wrong token type detected ({tokenType}). {hints?.[0] || ""}
        </p>
      )}
    </div>
  );
}

function AdminAuthCard({ authMethod, authSource, authOk, canWrite }) {
  // authMethod: "client_credentials" | "static" | "none"
  const isOk      = authOk && canWrite;
  const isMissing = authMethod === "none";

  const borderCls = isOk ? "border-green-500/20 bg-green-500/5"
    : isMissing    ? "border-red-500/20 bg-red-500/5"
    :                "border-yellow-500/20 bg-yellow-500/5";
  const iconCls   = isOk ? "text-green-400" : isMissing ? "text-red-400" : "text-yellow-400";
  const labelCls  = isOk ? "text-green-400" : isMissing ? "text-red-400" : "text-yellow-400";

  const badgeText =
    authMethod === "client_credentials" ? "client_credentials" :
    authMethod === "static"             ? "static token"        :
                                          "missing";
  const badgeCls  = isOk
    ? "bg-green-500/15 text-green-400"
    : "bg-red-500/15 text-red-400";

  return (
    <div className={`rounded border p-3 ${borderCls}`} data-testid="admin-auth-card">
      <div className="flex items-center gap-2 mb-2">
        <Key size={11} className={iconCls} />
        <span className={`text-[10px] font-bold uppercase tracking-wider ${labelCls}`}>
          Admin Auth
        </span>
        <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded font-mono ${badgeCls}`}>
          {badgeText}
        </span>
      </div>

      {/* Source line */}
      {authSource && (
        <p className="text-[10px] text-white/45 mb-1">
          Source: <span className="font-mono text-white/60">{authSource}</span>
        </p>
      )}

      {/* Status line */}
      {isOk && (
        <p className="text-[10px] text-green-400/70">
          Admin API authenticated — product writes, metaobject sync, and file upload active.
        </p>
      )}
      {!isOk && authMethod === "client_credentials" && (
        <p className="text-[10px] text-yellow-400/70">
          Client credentials configured but admin API test failed. Check SHOPIFY_CLIENT_ID / SHOPIFY_CLIENT_SECRET.
        </p>
      )}
      {isMissing && (
        <p className="text-[10px] text-red-400/70">
          No admin credentials found. Add <span className="font-mono">SHOPIFY_CLIENT_ID + SHOPIFY_CLIENT_SECRET</span> (Dev Dashboard app) or <span className="font-mono">SHOPIFY_ADMIN_TOKEN=shpat_...</span> (Custom App) to backend .env.
        </p>
      )}
    </div>
  );
}

export default function AdminShopify() {
  const [status, setStatus]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncLog, setSyncLog]     = useState([]);

  /* Storefront Token Regeneration */
  const [sfStatus, setSfStatus]       = useState(null);
  const [sfRegenerating, setSfRegenerating] = useState(false);
  const [sfResult, setSfResult]       = useState(null);
  const [sfShowToken, setSfShowToken] = useState(false);
  const [sfCopied, setSfCopied]       = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/api/admin/shopify/status");
      setStatus(res.data);
    } catch (err) {
      console.error("[AdminShopify] fetch status failed:", err);
      setStatus({ connection: "failed", domain: "(error)", misconfig_hints: [] });
    }
    setLoading(false);
  }, []);

  const fetchSyncLog = useCallback(async () => {
    try {
      const res = await adminApi.get("/api/admin/shopify/sync-log?limit=10");
      setSyncLog(res.data.entries || []);
    } catch (err) { console.error("[AdminShopify] fetch sync log failed:", err); }
  }, []);

  const fetchSfStatus = useCallback(async () => {
    try {
      const res = await adminApi.get("/api/admin/shopify/storefront-token-status");
      setSfStatus(res.data);
    } catch (err) { console.error("[AdminShopify] fetch storefront status failed:", err); }
  }, []);

  useEffect(() => { fetchStatus(); fetchSyncLog(); fetchSfStatus(); }, [fetchStatus, fetchSyncLog, fetchSfStatus]);

  const handleRegenStorefront = async () => {
    if (!window.confirm(
      "This will generate a NEW storefront token via the Shopify Admin API.\n\n" +
      "Your existing token will remain active (Shopify supports multiple tokens).\n\n" +
      "You'll need to copy the new token into your .env to use it.\n\nProceed?"
    )) return;
    setSfRegenerating(true);
    setSfResult(null);
    setSfShowToken(false);
    setSfCopied(false);
    try {
      const res = await adminApi.post("/api/admin/shopify/regenerate-storefront-token");
      setSfResult(res.data);
      fetchSfStatus();
    } catch (err) {
      setSfResult({ success: false, error: err.response?.data?.detail || err.message });
    }
    setSfRegenerating(false);
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    setSfCopied(true);
    setTimeout(() => setSfCopied(false), 2000);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await adminApi.post("/api/admin/shopify/sync");
      setSyncResult(res.data);
      fetchSyncLog();
      fetchStatus();
    } catch (err) {
      setSyncResult({ status: "error", errors: [err.response?.data?.detail || err.message] });
    }
    setSyncing(false);
  };

  if (loading) return (
    <div className="border border-white/[0.08] bg-[#060606] p-8 text-center text-xs text-white/30">
      Loading Shopify status...
    </div>
  );

  const s               = status || {};
  const isConnected     = (s.connection || "").includes("connected");
  const adminOk         = s.admin_auth_ok || (s.admin_token_present && s.admin_token_type === "admin" && s.can_write_products);
  const sfOk            = s.sf_token_present;
  const hints           = s.misconfig_hints || [];
  const graphqlInactive = s.storefront_token_status === "graphql_inactive";

  return (
    <div className="space-y-6" data-testid="admin-shopify">

      {/* ── Connection Overview ─────────────────────────────────────────── */}
      <div className="border border-white/[0.08] bg-[#060606]" data-testid="shopify-status-panel">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Store size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Shopify Connection</h3>
          <div className="ml-auto flex items-center gap-2">
            <StatusDot status={isConnected ? "ok" : "bad"} />
            <span className={`text-xs font-semibold ${isConnected ? "text-green-400" : "text-red-400"}`}>
              {s.connection || "Disconnected"}
            </span>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Domain + counts */}
          <div>
            <DiagRow
              label="Store Domain"
              value={s.domain}
              status={s.domain_configured ? "ok" : "bad"}
            />
            <DiagRow label="Products"    value={String(s.product_count || 0)}    status={s.product_count > 0  ? "ok" : undefined} />
            <DiagRow label="Collections" value={String(s.collection_count || 0)} status={s.collection_count > 0 ? "ok" : undefined} />
            {s.shop_name  && <DiagRow label="Shop Name" value={s.shop_name}  />}
            {s.shop_email && <DiagRow label="Email"     value={s.shop_email} />}
            {s.currency   && <DiagRow label="Currency"  value={s.currency}   />}
          </div>

          {/* Token cards — admin auth and storefront shown separately */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="token-status-cards">
            <AdminAuthCard
              authMethod={s.admin_auth_method || (s.admin_token_present ? "static" : "none")}
              authSource={s.admin_auth_source}
              authOk={s.admin_auth_ok}
              canWrite={s.can_write_products}
            />
            <TokenCard
              title="Storefront Token"
              present={s.sf_token_present}
              tokenType={s.sf_token_type}
              isCorrectType={["storefront", "custom-app", "storefront-legacy"].includes(s.sf_token_type)}
              testOk={s.can_read_storefront_gql !== false}
              description="shpss_ / legacy token — used for storefront GraphQL reads (shop metadata). Optional if using public REST."
              hints={[]}
            />
          </div>

          {/* Capabilities */}
          {s.api_capabilities?.length > 0 && (
            <div className="p-3 rounded bg-green-500/5 border border-green-500/10">
              <div className="flex items-center gap-2 text-xs text-green-400 mb-2">
                <Shield size={12} />
                <span className="font-semibold">Active Capabilities</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {s.api_capabilities.map((cap) => (
                  <span key={cap} className="text-[10px] px-2 py-0.5 rounded bg-green-500/10 text-green-400/80 border border-green-500/15">{cap}</span>
                ))}
              </div>
            </div>
          )}

          {/* Mis-config hints */}
          {hints.length > 0 && (
            <div className="p-3 rounded bg-yellow-500/8 border border-yellow-500/20" data-testid="misconfig-hints">
              <div className="flex items-center gap-2 text-xs text-yellow-400 mb-2">
                <AlertCircle size={12} />
                <span className="font-semibold">Configuration hints</span>
              </div>
              <ul className="space-y-1">
                {hints.map((h) => (
                  <li key={h} className="text-[10px] text-yellow-400/70 leading-relaxed">{h}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Soft note: GraphQL inactive but REST working */}
          {graphqlInactive && !hints.some(h => h.includes("storefront")) && (
            <div className="p-3 rounded bg-white/[0.02] border border-white/[0.06] flex items-start gap-2.5">
              <Info size={12} className="text-white/30 mt-0.5 shrink-0" />
              <p className="text-[10px] text-white/30 leading-relaxed">
                Storefront GraphQL token inactive &mdash; shop name lookup unavailable. Product sync,
                cart, and all storefront reads use the public REST API and are working normally.
              </p>
            </div>
          )}

          {/* Admin token missing — prominent call-to-action (hidden when client_credentials auth is active) */}
          {!adminOk && isConnected && (
            <div className="p-3 rounded bg-[#CBA052]/8 border border-[#CBA052]/25" data-testid="admin-token-cta">
              <div className="flex items-center gap-2 text-xs text-[#CBA052] mb-1">
                <Key size={12} />
                <span className="font-bold">Admin token required for canvas publishing</span>
              </div>
              <p className="text-[10px] text-[#CBA052]/60 leading-relaxed">
                Add <code className="bg-white/10 px-1 rounded font-mono">SHOPIFY_ADMIN_TOKEN=shpat_...</code> to{" "}
                <code className="bg-white/10 px-1 rounded font-mono">backend/.env</code> to enable:<br />
                product create/update, canvas_artwork metaobject sync, and file upload.
              </p>
              <p className="text-[10px] text-white/30 mt-1">
                Get it from Shopify Admin &rarr; Settings &rarr; Apps &rarr; Develop apps &rarr; your app &rarr; API credentials.
                Scopes needed: <code className="font-mono">write_products, write_metaobjects, read_files, write_files</code>.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Storefront Token Management ────────────────────────────── */}
      <div className="border border-white/[0.08] bg-[#060606]" data-testid="storefront-token-panel">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Key size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Storefront Token</h3>
          {sfStatus?.present && (
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/15 font-mono">
              {sfStatus.token_type}
            </span>
          )}
        </div>
        <div className="p-5 space-y-4">
          {/* Current status */}
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <StatusDot status={sfStatus?.present ? "ok" : "warn"} />
                <span className={`text-xs font-medium ${sfStatus?.present ? "text-green-400" : "text-yellow-400"}`}>
                  {sfStatus?.present ? "Token configured" : "No token configured"}
                </span>
              </div>
              {sfStatus?.present && (
                <p className="text-[10px] text-white/35 font-mono">{sfStatus.token_prefix}</p>
              )}
              <p className="text-[10px] text-white/25 mt-1">
                Used for Storefront GraphQL reads. Optional if products are fetched via public REST API.
              </p>
            </div>
          </div>

          {/* Warning box */}
          <div className="flex items-start gap-2.5 p-3 rounded bg-yellow-500/[0.04] border border-yellow-500/10">
            <AlertTriangle size={12} className="text-yellow-400/60 mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] text-yellow-400/60 leading-relaxed">
                Regenerating creates a <strong>new</strong> storefront token. The old token remains active until manually deleted from Shopify.
                Any external integration (Hydrogen, Gatsby, etc.) using the old token will continue working.
              </p>
              <p className="text-[10px] text-white/25 mt-1">
                After generation, copy the new token into <code className="font-mono bg-white/10 px-1 rounded">SHOPIFY_STOREFRONT_TOKEN</code> in <code className="font-mono bg-white/10 px-1 rounded">backend/.env</code> and restart the backend.
              </p>
            </div>
          </div>

          {/* Action button */}
          <button
            onClick={handleRegenStorefront}
            disabled={sfRegenerating || !adminOk}
            className="flex items-center gap-2 text-xs font-bold text-black bg-[#CBA052] px-5 py-2.5 rounded hover:opacity-90 disabled:opacity-40 transition-opacity"
            data-testid="regenerate-storefront-btn"
          >
            <RefreshCw size={13} className={sfRegenerating ? "animate-spin" : ""} />
            {sfRegenerating ? "Generating..." : "Generate New Storefront Token"}
          </button>
          {!adminOk && (
            <p className="text-[10px] text-white/25">Admin API auth is required to generate storefront tokens.</p>
          )}

          {/* Result */}
          {sfResult && (
            <div className={`p-3 rounded border ${sfResult.success ? "bg-green-500/10 border-green-500/20" : "bg-red-500/10 border-red-500/20"}`} data-testid="storefront-regen-result">
              {sfResult.success ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-green-400">
                    <CheckCircle size={13} />
                    <span className="font-semibold">Storefront token generated</span>
                  </div>
                  <p className="text-[10px] text-white/35">
                    Title: <span className="font-mono text-white/50">{sfResult.title}</span>
                  </p>

                  {/* Secure token display */}
                  <div className="flex items-center gap-2 p-2 rounded bg-black/40 border border-white/[0.06]">
                    <code className="flex-1 text-[10px] font-mono text-green-400/80 break-all">
                      {sfShowToken ? sfResult.full_token : sfResult.token_prefix}
                    </code>
                    <button
                      onClick={() => setSfShowToken((v) => !v)}
                      className="p-1 text-white/30 hover:text-white/60 transition-colors"
                      title={sfShowToken ? "Hide token" : "Reveal token"}
                    >
                      {sfShowToken ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                    {sfShowToken && (
                      <button
                        onClick={() => copyToken(sfResult.full_token)}
                        className="p-1 text-white/30 hover:text-white/60 transition-colors"
                        title="Copy to clipboard"
                      >
                        {sfCopied ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
                      </button>
                    )}
                  </div>

                  <p className="text-[10px] text-white/25">
                    {sfResult.instructions}
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <XCircle size={13} className="text-red-400" />
                  <span className="text-xs font-semibold text-red-400">Generation failed</span>
                  <span className="text-[10px] text-red-400/70 ml-2">{sfResult.error}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Product Sync ────────────────────────────────────────────────── */}
      <div className="border border-white/[0.08] bg-[#060606]" data-testid="shopify-sync-panel">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <RefreshCw size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Product Sync</h3>
        </div>
        <div className="p-5">
          <p className="text-xs text-white/40 mb-4">
            Pull published products from Shopify into the local database.
            Uses the public REST API &mdash; no admin token required.
          </p>
          <button
            onClick={handleSync}
            disabled={syncing || !isConnected}
            className="flex items-center gap-2 text-xs font-bold text-black bg-[#CBA052] px-5 py-2.5 rounded hover:opacity-90 disabled:opacity-40 transition-opacity"
            data-testid="shopify-sync-btn"
          >
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing..." : "Sync Now"}
          </button>

          {syncResult && (
            <div
              className={`mt-4 p-3 rounded border ${syncResult.status === "completed" ? "bg-green-500/10 border-green-500/20" : "bg-red-500/10 border-red-500/20"}`}
              data-testid="sync-result"
            >
              <div className="flex items-center gap-2 text-xs">
                {syncResult.status === "completed"
                  ? <CheckCircle size={13} className="text-green-400" />
                  : <XCircle    size={13} className="text-red-400" />}
                <span className={`font-semibold ${syncResult.status === "completed" ? "text-green-400" : "text-red-400"}`}>
                  {syncResult.status === "completed"
                    ? `Synced ${syncResult.products_synced} products`
                    : "Sync failed"}
                </span>
              </div>
              {syncResult.errors?.map((e) => (
                <p key={e} className="text-[10px] text-red-400/70 mt-1">{e}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Sync History ────────────────────────────────────────────────── */}
      <div className="border border-white/[0.08] bg-[#060606]" data-testid="shopify-sync-log">
        <div className="p-5 border-b border-white/[0.06] flex items-center gap-3">
          <Clock size={16} className="text-[#CBA052]" />
          <h3 className="text-sm font-bold text-white">Sync History</h3>
        </div>
        <div className="p-5">
          {syncLog.length === 0 ? (
            <p className="text-xs text-white/30 text-center py-4">No sync history yet.</p>
          ) : (
            <div className="space-y-2">
              {syncLog.map((entry) => (
                <div key={entry.timestamp || entry.id || JSON.stringify(entry)} className="flex items-center gap-3 py-2 border-b border-white/[0.04] last:border-0">
                  {entry.status === "completed"
                    ? <CheckCircle size={12} className="text-green-400 shrink-0" />
                    : <XCircle    size={12} className="text-red-400 shrink-0" />}
                  <span className="text-[11px] text-white/60 flex-1">{entry.products_synced || 0} products synced</span>
                  <span className="text-[10px] text-white/25">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
